/* eslint-disable no-nested-ternary */
/* eslint-disable react/require-default-props */

import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Model, { Providers } from '@tripian/model';
import { providers } from '@tripian/core';
import { StepInfo, PoiInfo, AccommendationInfo, Modal, Booking, Button, PreLoading, CloseIconButton, ReservationDetails } from '@tripian/react';

import './FocusContainer.scss';
import config from '../../../../../config';

import useTrip from '../../../../../hooks/useTrip';
import usePlan from '../../../../../hooks/usePlan';
import useStep from '../../../../../hooks/useStep';
import useFavorite from '../../../../../hooks/useFavorite';
import useReservation from '../../../../../hooks/useReservation';
import useFocus from '../../../../../hooks/useFocus';
import useUser from '../../../../../hooks/useUser';

interface IFocusContainer {
  // show: boolean;
  planId?: number;
  // focus: { step?: Model.Step; alternative?: { poi: Model.Poi; stepId: number }; search?: Model.Poi };
  showTourInfoModal: (id: string) => void;
}

const FocusContainer: React.FC<IFocusContainer> = ({ /* show, */ planId, /* focus, */ showTourInfoModal }) => {
  const [bookingModalState, setBookingModalState] = useState<{
    show: boolean;
    businessId: string;
    isEditMode: boolean;
    poi?: Model.Poi;
  }>({
    show: false,
    businessId: '',
    isEditMode: false,
  });

  const { tripReference } = useTrip();
  const { plans } = usePlan();
  const { stepAdd, stepDelete, stepReplace } = useStep();
  const { favorites, favoriteAdd, favoriteDelete, loadingFavoritePoi } = useFavorite();
  const { reservationAdd, loadingReservation, yelpReservations, reservationCancel } = useReservation();
  const { focus, focusLost } = useFocus();
  const { user } = useUser();

  const planDate: string | undefined = useMemo(() => plans?.find((currentPlan) => currentPlan.id === planId)?.date, [planId, plans]);

  const { hash } = useParams<{ hash: string }>();

  const focusContainerClasses = ['focusContainer'];
  if (config.HIDE_OVERVIEW) focusContainerClasses.push('container-height');
  else focusContainerClasses.push('container-height-tab');
  if (focus.step || focus.alternative || focus.poi) focusContainerClasses.push('focusContainerOpen');
  else focusContainerClasses.push('focusContainerClose');

  const isFavorite = (poiId: number) => (favorites ? favorites.findIndex((favorite) => favorite.poi_id === poiId) > -1 : false);
  const favoriteToggle = (add: boolean, poiId: number) => {
    if (add) {
      favoriteAdd(poiId);
    } else {
      const favoriteId: number = favorites?.find((favorite) => favorite.poi_id === poiId)?.id || 0;
      if (favoriteId) favoriteDelete(favoriteId, poiId);
      // eslint-disable-next-line no-console
      else console.error('favoriteToggle favorite delete error: Can not found poi id in favorite list', add, poiId, favorites);
    }
  };

  const getPartOfDay = useCallback(() => {
    const findDaysNumbers = (poiId: number): number[] | undefined => {
      if (poiId < 1) return undefined;

      if (plans) {
        const partOfDayIndexes: number[] = [];
        for (let i = 0; i < plans.length; i += 1) {
          const stepIndex = plans[i].steps.findIndex((step) => step.poi.id === poiId);
          if (stepIndex > -1) {
            // console.log('getPartOfDay dayNumber', stepIndex + 1);
            partOfDayIndexes.push(i + 1);
            // return i + 1;
          }
        }
        return partOfDayIndexes;
      }
      return undefined;
    };

    if (focus.step) {
      return findDaysNumbers(focus.step.poi.id);
    }

    if (focus.alternative) {
      return findDaysNumbers(focus.alternative.poi.id);
    }

    if (focus.poi) {
      return findDaysNumbers(focus.poi.id);
    }

    return undefined;
  }, [focus.alternative, focus.poi, focus.step, plans]);

  const memoizedBookingClicked = useCallback((productId: string, poi: Model.Poi) => {
    const openTableProduct = poi.booking.find((b) => b.provider_id === 5)?.product;
    if (openTableProduct) window.open(openTableProduct.url || 'https://www.opentable.com');
    else setBookingModalState({ show: true, businessId: productId, isEditMode: false, poi });
  }, []);

  const memoizedReservationRequest = useCallback((reservationModel: Providers.Yelp.ReservationRequest) => providers.yelp?.reservation(reservationModel) as Promise<Providers.Yelp.Reservation>, []);

  const memoizedBookingCallback = useCallback(
    (provider: string, poi: Model.Poi, reservationDetails: Providers.Yelp.ReservationRequest, response?: Providers.Yelp.Reservation) => {
      if (response) {
        const reservationInfo: Providers.Yelp.ReservationInfo = {
          confirm_url: response.confirmation_url,
          reservation_id: response.reservation_id || '',
          restaurant_image: poi.image.url,
          restaurant_name: poi.name,
          reservation_details: reservationDetails,
        };

        reservationAdd({ key: Model.PROVIDER_NAME.YELP, trip_hash: hash, poi_id: poi.id, provider, value: reservationInfo }, false).finally(() => {
          setBookingModalState({ show: false, businessId: '', isEditMode: false });
        });
      }

      return null;
    },
    [hash, reservationAdd],
  );

  const memoizedCurrentReservationUrl = useCallback(
    (poiId: number) => {
      const currentUserReservation = yelpReservations?.find((reservation) => reservation.poi_id === poiId)?.value as Providers.Yelp.ReservationInfo;

      if (currentUserReservation) {
        return currentUserReservation.confirm_url;
      }

      return undefined;
    },
    [yelpReservations],
  );

  // YelpReservation Api Requests for inner form
  const yelpGetBusinesInfo = useCallback((businessId: string) => providers.yelp?.business(businessId) as Promise<Providers.Yelp.Business>, []);

  const yelpOpeningRequest = useCallback((openings: Providers.Yelp.OpeningsRequest) => providers.yelp?.openings(openings) as Promise<Providers.Yelp.Openings>, []);

  const yelpGetHoldIt = useCallback((hold: Providers.Yelp.HoldRequest) => providers.yelp?.hold(hold) as Promise<Providers.Yelp.Hold>, []);

  const memoizedBookingModal = useMemo(() => {
    const currentReservation = yelpReservations?.find((reservation) => reservation.poi_id === bookingModalState.poi?.id);
    const coversCount = (tripReference?.trip_profile.number_of_adults || 0) + (tripReference?.trip_profile.number_of_children || 0);
    const userProfileBooking: { firstName: string; lastName: string; email: string; coversCount: number } = {
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      email: user?.email || '',
      coversCount,
    };

    return (
      <Modal
        show={bookingModalState.show}
        className="booking-modal p5"
        backdropClick={() => {
          if (!loadingReservation) setBookingModalState({ show: false, businessId: '', isEditMode: false });
        }}
      >
        <div>
          <div className="booking-modal-close-icon m1">
            <CloseIconButton
              fill="#fff"
              clicked={() => {
                if (!loadingReservation) setBookingModalState({ show: false, businessId: '', isEditMode: false });
              }}
            />
          </div>
          {loadingReservation ? (
            <div className="create-update-trip-loading">
              <PreLoading bgColor="rgba(238, 238, 238, 0.8)" />
            </div>
          ) : null}
          <div>
            {currentReservation && !bookingModalState.isEditMode ? (
              <ReservationDetails reservationInfo={currentReservation.value as Providers.Yelp.ReservationInfo} />
            ) : bookingModalState.poi && bookingModalState.show ? (
              <Booking
                businessId={bookingModalState.businessId}
                poi={bookingModalState.poi}
                stepDate={planDate || ''}
                stepHour={focus.step?.hours.from}
                userProfileBooking={userProfileBooking}
                getBusinessInfo={yelpGetBusinesInfo}
                openingsRequest={yelpOpeningRequest}
                getHoldIt={yelpGetHoldIt}
                reservationRequest={memoizedReservationRequest}
                bookingCallback={memoizedBookingCallback}
              />
            ) : null}
          </div>
          {currentReservation && !bookingModalState.isEditMode ? (
            <div className="row center">
              <div>
                <Button
                  color="primary"
                  text="Cancel Reservation"
                  onClick={() => {
                    setBookingModalState((prevState) => ({
                      ...prevState,
                    }));
                    reservationCancel((currentReservation?.value as Providers.Yelp.ReservationInfo).reservation_id)?.finally(() => {
                      setBookingModalState({ show: false, businessId: '', isEditMode: false });
                    });
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>
      </Modal>
    );
  }, [
    yelpReservations,
    tripReference,
    user,
    bookingModalState.show,
    bookingModalState.isEditMode,
    bookingModalState.poi,
    bookingModalState.businessId,
    loadingReservation,
    planDate,
    focus.step,
    yelpGetBusinesInfo,
    yelpOpeningRequest,
    yelpGetHoldIt,
    memoizedReservationRequest,
    memoizedBookingCallback,
    reservationCancel,
  ]);

  const renderContent = () => {
    if (focus.step) {
      if (focus.step.id === 0) {
        const accommendation: Model.Accommodation = {
          ref_id: 'FocusContainer_renderContent_todo',
          name: focus.step.poi.name,
          address: focus.step.poi.description,
          coordinate: focus.step.poi.marker_coordinate,
        };
        return (
          <AccommendationInfo
            accommodation={accommendation}
            close={() => {
              focusLost();
            }}
          />
        );
      }

      return (
        <div className="scrollable-y">
          <StepInfo
            key={`focus_poi_${focus.step.id}`}
            dayNumbers={getPartOfDay() || [500]}
            step={focus.step}
            favorite={isFavorite(focus.step.poi.id)}
            toggleFavorite={(poi: Model.Poi, willFavorite: boolean) => {
              // console.log(`${poi.name} added to favorites..`);
              favoriteToggle(willFavorite, poi.id);
            }}
            close={() => {
              focusLost();
            }}
            removeStep={(stepId) => {
              focusLost();
              stepDelete(stepId);
            }}
            bookingButtonClick={memoizedBookingClicked}
            planDate={planDate && focus.step.hours.from ? `${planDate} ${focus.step.hours.from}` : undefined}
            favoriteLoading={loadingFavoritePoi(focus.step.poi.id)}
            reservationUrl={memoizedCurrentReservationUrl(focus.step.poi.id)}
            hideBookingButton={config.HIDE_RESTAURANT_RESERVATIONS}
            hideTours={config.HIDE_TOURS_AND_TICKETS}
            getTourInfo={showTourInfoModal}
            TOUR_PROVIDER_ID={config.TOUR_PROVIDER_SOURCE}
            TICKET_PROVIDER_ID={config.TICKET_PROVIDER_SOURCE}
          />
        </div>
      );
    }

    if (focus.alternative)
      return (
        <div className="scrollable-y">
          <PoiInfo
            dayNumbers={getPartOfDay()}
            key={`focus_alternative_poi_${focus.alternative.poi.id}`}
            poi={focus.alternative.poi}
            favorite={isFavorite(focus.alternative.poi.id)}
            toggleFavorite={(poi: Model.Poi, willFavorite: boolean) => {
              // console.log(`${poi.name} added to favorites..`);
              favoriteToggle(willFavorite, poi.id);
            }}
            close={() => {
              focusLost();
            }}
            addRemoveReplacePoi={(poi: Model.Poi, flag: number) => {
              focusLost();
              if (flag === 1) {
                // add poi to step
                console.error('FocusContainer: step add called by alternative poi!'); // eslint-disable-line no-console
              } else if (flag === 0) {
                // replace poi to step
                if (focus.alternative) {
                  focusLost();
                  stepReplace(focus.alternative.stepId, focus.alternative.poi.id);
                } else console.error('FocusContainer: step replace called without replaceStepId!'); // eslint-disable-line no-console
              } else if (flag === -1) {
                // remove poi from step
                // eslint-disable-next-line no-console
                console.error(
                  `FocusContainer:
                  PoiInfo never calls this remove step prop directly.
                  Only StepInfo component handle this event with StepInfo.removeStep`,
                );
              }
            }}
            replace
            favoriteLoading={loadingFavoritePoi(focus.alternative.poi.id)}
            bookingButtonClick={memoizedBookingClicked}
            reservationUrl={memoizedCurrentReservationUrl(focus.alternative.poi.id)}
            hideBookingButton={config.HIDE_RESTAURANT_RESERVATIONS}
            square={false}
            hideTours
            planDate={planDate}
          />
        </div>
      );

    if (focus.poi)
      return (
        <div className="scrollable-y">
          <PoiInfo
            key={`focus_search_poi_${focus.poi.id}`}
            dayNumbers={getPartOfDay()}
            poi={focus.poi}
            favorite={isFavorite(focus.poi.id)}
            toggleFavorite={(poi: Model.Poi, willFavorite: boolean) => {
              // console.log(`${poi.name} added to favorites..`);
              favoriteToggle(willFavorite, poi.id);
            }}
            close={() => {
              focusLost();
            }}
            addRemoveReplacePoi={(poi: Model.Poi, flag: number) => {
              focusLost();

              if (flag === 1) {
                // add poi to step
                if (planId) stepAdd(planId, poi.id);
                else console.error('FocusContainer: step add called without planId!'); // eslint-disable-line no-console
              } else if (flag === 0) {
                // replace poi to step
                // if (focus.poi) dispatch(stepReplace(focus.alternative.stepId, focus.alternative.poi.id));
                // else console.error('FocusContainer: step replace called without replaceStepId!'); // eslint-disable-line no-console
                console.error('FocusContainer: step replace called with same poi!'); // eslint-disable-line no-console
              } else if (flag === -1) {
                // remove poi from step
                // eslint-disable-next-line no-console
                console.error(
                  `FocusContainer:
                  PoiInfo never calls this remove step prop directly.
                  Only StepInfo component handle this event with StepInfo.removeStep`,
                );
              }
            }}
            replace={false}
            favoriteLoading={loadingFavoritePoi(focus.poi.id)}
            bookingButtonClick={memoizedBookingClicked}
            reservationUrl={memoizedCurrentReservationUrl(focus.poi.id)}
            hideBookingButton={config.HIDE_RESTAURANT_RESERVATIONS}
            square={false}
            hideTours
            planDate={planDate}
          />
        </div>
      );

    return null;
  };

  return (
    <>
      {memoizedBookingModal}
      <div className={focusContainerClasses.join(' ')}>{renderContent()}</div>
    </>
  );
};

export default FocusContainer;
